import { useEffect, useState, useContext, useRef, useMemo } from 'react';
import usePromise from 'react-use/lib/usePromise';
import { useLocation, useMatch, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { bindActionCreators } from 'redux';
import { useSelector, useDispatch } from 'react-redux';

import { initUrlFor } from './business';
import select from '../store/selectors';
import validate from '../store/validators';

export function usePromiseWrapper({
  initPromise,
  initialState = {},
  cached = () => false,
  propagateRejection = false,
}) {
  const [promiseIsRunning, setPromiseIsRunning] = useState(
    initialState.hasOwnProperty('loading') ? initialState.loading : !cached()
  );
  const [promiseError, setPromiseError] = useState(
    initialState.hasOwnProperty('error') ? initialState.error : undefined
  );

  const mounted = usePromise();

  async function _run(...args) {
    if (
      cached() ||
      (this && typeof this.cached === 'function' && this.cached())
    ) {
      if (typeof promiseError != 'undefined') {
        setPromiseError(undefined);
      }
      return;
    }

    if (!promiseIsRunning) {
      setPromiseIsRunning(true);
    }

    let resp;
    try {
      resp = await mounted(initPromise(...args));

      if (typeof promiseError != 'undefined') {
        setPromiseError(undefined);
      }
    } catch (error) {
      setPromiseError(error);

      if (propagateRejection) {
        setPromiseIsRunning(false);
        throw error;
      }
    }

    setPromiseIsRunning(false);

    return resp;
  }

  return [_run, promiseIsRunning, promiseError];
}

export function useRouter(options = {}) {
  const location = options.location || useLocation();
  const navigate = useNavigate();

  return { location, navigate };
}

export function useCustomTranslation() {
  const t = useTranslation();

  return {
    ...t,
    urlFor: initUrlFor(t.i18n.language),
  };
}

export function useLanguageChange() {
  const { i18n } = useTranslation();

  const { navigate, location } = useRouter();

  return (lng) => {
    const stripedLocation = location.pathname.slice(i18n.language.length + 1);

    navigate(`/${lng}${stripedLocation}${location.search}`, {replace: true});

    i18n.changeLanguage(lng);
  };
}

export function useComponentVisible(initialIsVisible) {
  const ref = useRef(null);

  const [isComponentVisible, setIsComponentVisible] = useState(
    initialIsVisible
  );

  function handleClickOutside(e) {
    if (ref.current != null && e.target !== ref.current) {
      setIsComponentVisible(false);
    }
  }
  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, []);
  return { ref, isComponentVisible, setIsComponentVisible };
}

export function useStoreSelector(selector, deps) {
  const _selector = (state) => {
    const s = select(state);

    return selector(s);
  };

  return useSelector(_selector, deps);
}

export function useStoreValidator(validator, deps) {
  const _validator = (state) => {
    const v = validate(state);

    return validator(v);
  };

  return useSelector(_validator, deps);
}

export function useCache(type, ...args) {
  const cacheValidator = (validator) => {
    if (typeof type == 'undefined') {
      return validator.cache.bind(validator);
    }

    return () => validator.cache(type, ...args);
  };

  return useStoreValidator(cacheValidator, [type, ...args]);
}

export function useActions(actions, deps) {
  var dispatch = useDispatch();
  return useMemo(function() {
    if (Array.isArray(actions)) {
      return actions.map(function(a) {
        return bindActionCreators(a, dispatch);
      });
    }

    return bindActionCreators(actions, dispatch);
  }, deps);
}
